import { assertNever } from "@@/shared/assert-never";
import { VersionMismatchError } from "@@/shared/errors/version-mismatch-event.error";
import { quoteFactory, type Quote } from "@@/shared/quotes/quote";
import type { QuoteEvent } from "@@/shared/quotes/quote-events/_quote-event";
import { missingQuoteEventCheck } from "@@/shared/quotes/quote-events/missing-quote-event-check";
import type { QuoteId } from "@@/shared/quotes/quote-id";
import { asArray } from "@@/utilities/as-array";

const checkQuoteId = (quote: Quote, event: QuoteEvent) => {
    if (quote._id !== event.quoteId) {
        throw new Error(
            `Quote id mismatch, cannot apply event ${event._id}|${event.type}|${event.quoteId} on quote ${quote._id}`,
        );
    }
};

const firstEventMustBeAnQuoteCreatedEventCheck = (
    quoteId: QuoteId,
    events: QuoteEvent[],
) => {
    if (events[0]?.type !== "CREATED") {
        throw new Error(
            `The first event for an order must be CREATED_EVENT; QuoteId: ${quoteId}, eventCount: ${events.length}`,
        );
    }
};

const QuoteReducer = (
    QuoteEvents: QuoteEvent | QuoteEvent[],
    initialQuote?: Quote,
): Quote => {
    const events = asArray(QuoteEvents);
    const startAtVersion =
        typeof initialQuote?._version === "number"
            ? initialQuote._version + 1
            : 0;

    const quoteId = initialQuote?._id ?? events?.[0]?.quoteId;
    if (!initialQuote) {
        firstEventMustBeAnQuoteCreatedEventCheck(quoteId, events);
    }

    const sortedEvents = events
        .filter(item => item.quoteId === quoteId)
        .filter(item => item._version >= startAtVersion)
        .sort((a, b) => a._version - b._version);
    missingQuoteEventCheck(quoteId, sortedEvents, { startAtVersion });

    const result = sortedEvents.reduce((projection, event) => {
        if (
            event.type !== "CREATED" &&
            event._version !== projection._version + 1
        )
            throw new VersionMismatchError(
                `Can't apply event with version ${event._version} to order at version ${projection._version}; ${projection._id}`,
            );

        if (event.type === "CREATED" && event._version !== 0) {
            throw new Error(
                `Can't apply a created event to order with a version of ${event._version}, needs to be 0; ${quoteId}`,
            );
        }

        switch (event.type) {
            case "CREATED": {
                return quoteFactory({
                    _id: event.quoteId,
                    _createdAt: event._createdAt,
                    _updatedAt: event._createdAt,
                    _status: "ACTIVE",
                    valid: event.quote.valid,
                    _createdBy: event._createdBy,
                    _version: 0,
                    providerId: event.providerId,
                    providerSlug: event.providerSlug,
                    title: event.quote.title,
                    state: "DRAFT",
                    recipients: [],
                    customer: event.quote.customer,
                    items: event.quote.items,
                    reference: "",
                    viewCount: 0,
                    published: [],
                    lastEditVersion: 0,
                });
            }
            case "UPDATED": {
                if (projection.state === "ACCEPTED") {
                    throw new Error(
                        `Can't update an accepted quote; ${quoteId}`,
                    );
                }

                return {
                    ...projection,
                    _version: event._version,
                    _updatedAt: event._createdAt,
                    title: event.update.title,
                    items: event.update.items,
                    customer: event.update.customer,
                    recipients: event.update.recipients,
                    valid: event.update.valid,
                    reference: event.update.reference,
                    lastEditVersion: event._version,
                } as Quote;
            }
            case "ACCEPTED": {
                return {
                    ...projection,
                    _version: event._version,
                    _updatedAt: event._createdAt,
                    state: "ACCEPTED",
                    accepted: {
                        acceptedAt: event._createdAt,
                        acceptedVersion: event._version,
                        name: event.acceptedBy.name,
                        email: event.acceptedBy.email,
                    },
                } as Quote;
            }
            case "PUBLISHED": {
                return {
                    ...projection,
                    _version: event._version,
                    _updatedAt: event._createdAt,
                    state: "PUBLISHED",
                    published: [
                        ...projection.published,
                        {
                            publishedAt: event._createdAt,
                            publishedVersion: event._version,
                        },
                    ],
                } as Quote;
            }
            case "DELETED":
                return {
                    ...projection,
                    _version: event._version,
                    _updatedAt: event._createdAt,
                    _status: "REMOVED",
                } as Quote;
            case "VIEWED":
                return {
                    ...projection,
                    _version: event._version,
                    viewCount: projection.viewCount + 1,
                    viewedLast: event._createdAt,
                } as Quote;
            default: {
                assertNever(event);
            }
        }
    }, {} as Quote);

    //Should we pass trough quote zod?
    return result;
};

export { checkQuoteId, QuoteReducer };
